/*
 *  Copyright 2019-2022 LiteFarm.org
 *  This file is part of LiteFarm.
 *
 *  LiteFarm is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  LiteFarm is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 *  GNU General Public License for more details, see <https://www.gnu.org/licenses/>.
 */

import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory, matchPath } from 'react-router-dom';
import clsx from 'clsx';
import styles from './styles.module.scss';
import PureNavigation from '../../components/Navigation';
import { showedSpotlightSelector } from '../showedSpotlightSlice';
import { setSpotlightToShown } from '../Map/saga';
import useIsFarmSelected from '../../hooks/useIsFarmSelected';
import { CUSTOM_SIGN_UP } from '../CustomSignUp/constants';
import ReleaseBadgeHandler from '../ReleaseBadgeHandler';
import { useIsOffline } from '../hooks/useOfflineDetector/useIsOffline';
import OfflineIndicator from '../OfflineIndicator';

const Navigation = ({ children, ...props }) => {
  const offline = useIsOffline();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isFarmSelected = useIsFarmSelected();
  const ACCEPTING_INVITE_URLS = ['/accept_invitation/sign_up', '/accept_invitation/create_account'];
  const isAcceptingInvite = ACCEPTING_INVITE_URLS.some((path) =>
    matchPath(location.pathname, path),
  );
  const isLoginPage = location.state?.component === CUSTOM_SIGN_UP;
  // Hides the top navigation bar with logo on the login component
  const showNav = !isLoginPage;
  // Shows the navigation links when farm is selected and not accepting an farm invitation
  const showNavActions = isFarmSelected && !isAcceptingInvite;
  const { navigation, notification } = useSelector(showedSpotlightSelector);
  const resetSpotlight = () => {
    dispatch(setSpotlightToShown(['notification', 'navigation']));
  };

  return (
    <div className={clsx(styles.navigationWrapper, offline && styles.offlineMode)}>
      <OfflineIndicator />
      <PureNavigation
        showNavigationSpotlight={!navigation}
        showNotificationSpotlight={navigation && !notification}
        resetSpotlight={resetSpotlight}
        history={history}
        showNavActions={showNavActions}
        showNav={showNav}
        {...props}
      >
        {children}
      </PureNavigation>
      {isFarmSelected && <ReleaseBadgeHandler {...props} />}
    </div>
  );
};

export default Navigation;
